import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  FileText, CheckCircle2, AlertTriangle, Loader2, ExternalLink, FolderOpen,
  Upload, Trash2, Send, X, FileWarning,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import RequestAdditionalDocsDialog from './RequestAdditionalDocsDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { value: 'kbis',      label: 'Extrait Kbis' },
  { value: 'urssaf',    label: 'Attestation vigilance URSSAF' },
  { value: 'identite',  label: "Pièce d'identité du représentant" },
  { value: 'fiscale',   label: 'Attestation de régularité fiscale' },
  { value: 'decennale', label: 'Attestation Décennale / RC Pro' },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch (_e) { return iso; }
};

const statusStyle = (status) => {
  switch (status) {
    case 'ok':      return { pill: 'bg-green-100 text-green-700', icon: 'text-green-600',  bg: 'bg-green-50' };
    case 'warning': return { pill: 'bg-orange-100 text-orange-700', icon: 'text-orange-600', bg: 'bg-orange-50' };
    case 'expired': return { pill: 'bg-red-100 text-red-700',     icon: 'text-red-600',    bg: 'bg-red-50' };
    default:        return { pill: 'bg-slate-100 text-slate-500', icon: 'text-slate-400',  bg: 'bg-slate-50' };
  }
};

/**
 * Documents légaux du sous-traitant. Permet :
 * - upload manuel avec choix du type + date d'édition (départ des 3 mois)
 * - suppression d'un document manuel
 * - envoi d'un mail au sous-traitant avec lien d'upload direct
 */
export default function SubcontractorDocumentsPanel({ sousTraitantId, onRefresh }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [upType, setUpType] = useState('kbis');
  const [upDate, setUpDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [upFile, setUpFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Renewal request state
  const [sendingRenewal, setSendingRenewal] = useState(false);
  // Custom-request state (checkbox + message)
  const [customReqOpen, setCustomReqOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sous-traitants/${sousTraitantId}/documents`);
      setDocs(r.data.documents || []);
    } catch (_e) {
      setDocs([]);
    }
    setLoading(false);
  }, [sousTraitantId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!upFile) {
      toast.error('Sélectionnez un fichier');
      return;
    }
    if (!upDate) {
      toast.error("Renseignez la date d'édition du document");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', upFile);
      fd.append('doc_type', upType);
      fd.append('edition_date', upDate);
      await api.post(`/sous-traitants/${sousTraitantId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document ajouté');
      setUploadOpen(false);
      setUpFile(null);
      setUpDate(new Date().toISOString().slice(0, 10));
      setUpType('kbis');
      await load();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'ajout");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Supprimer ${doc.label} ?`)) return;
    try {
      await api.delete(`/sous-traitants/${sousTraitantId}/documents/${doc.type}`);
      toast.success('Document supprimé');
      await load();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Échec de la suppression');
    }
  };

  const handleSendRenewal = async () => {
    setSendingRenewal(true);
    try {
      const r = await api.post(
        `/sous-traitants/${sousTraitantId}/documents/send-renewal`,
        { doc_types: null }, // laisse le back détecter les docs à renouveler
      );
      toast.success(
        `Email envoyé à ${r.data.sent_to} · ${r.data.doc_types.length} document(s) demandé(s)`,
        { duration: 8000 },
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'envoi", { duration: 10000 });
    } finally {
      setSendingRenewal(false);
    }
  };

  const hasExpiring = docs.some((d) => d.status === 'warning' || d.status === 'expired');

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="subcontractor-documents-panel">
      <div className="p-5 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-blue-600" />
          <h3 className="font-bold text-slate-900">Documents légaux</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setCustomReqOpen(true)}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="st-doc-request-custom-btn"
            title="Choisir précisément les documents à redemander + ajouter un message personnalisé"
          >
            <FileWarning className="w-3.5 h-3.5 mr-1.5" />
            Redemander (sélection)
          </Button>
          {hasExpiring && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSendRenewal}
              disabled={sendingRenewal}
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
              data-testid="st-doc-send-renewal-btn"
              title="Envoie un email au sous-traitant avec un lien pour déposer ses documents à jour"
            >
              {sendingRenewal
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <Send className="w-3.5 h-3.5 mr-1.5" />}
              Demander renouvellement
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            onClick={() => setUploadOpen(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="st-doc-add-btn"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            Ajouter document
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
        </div>
      ) : (
        <div className="divide-y divide-slate-50">
          {docs.map((d) => {
            const s = statusStyle(d.status);
            return (
              <div key={d.type} className="p-4 flex items-center gap-3" data-testid={`st-doc-${d.type}`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${s.bg}`}>
                  {d.url ? (
                    d.status === 'expired' ? (
                      <AlertTriangle className={`w-5 h-5 ${s.icon}`} />
                    ) : d.status === 'warning' ? (
                      <AlertTriangle className={`w-5 h-5 ${s.icon}`} />
                    ) : (
                      <CheckCircle2 className={`w-5 h-5 ${s.icon}`} />
                    )
                  ) : (
                    <FileText className="w-5 h-5 text-slate-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-900 text-sm">{d.label}</p>
                    {d.status === 'expired' && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.pill}`}>
                        Expiré depuis {Math.abs(d.days_left || 0)} j
                      </span>
                    )}
                    {d.status === 'warning' && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.pill}`}>
                        Expire dans {d.days_left} j
                      </span>
                    )}
                    {d.status === 'ok' && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.pill}`}>
                        Valide{d.expires_at ? ` jusqu'au ${formatDate(d.expires_at)}` : ''}
                      </span>
                    )}
                    {d.status === 'missing' && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.pill}`}>
                        Non transmis
                      </span>
                    )}
                    {d.source === 'manual' && (
                      <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        Ajouté manuellement
                      </span>
                    )}
                  </div>
                  {d.url ? (
                    <p className="text-xs text-slate-500 mt-0.5 truncate">
                      {d.filename}
                      {d.edition_date && ` · édité le ${formatDate(d.edition_date)}`}
                      {!d.edition_date && d.uploaded_at && ` · reçu le ${formatDate(d.uploaded_at)}`}
                    </p>
                  ) : (
                    <p className="text-xs text-slate-400 mt-0.5">
                      {d.max_age_days
                        ? `Doit dater de moins de ${d.max_age_days} jours`
                        : "Document requis (pas d'expiration)"}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {d.url && (
                    <a
                      href={`${BACKEND_URL}${d.url}`}
                      target="_blank" rel="noopener noreferrer"
                      className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Ouvrir le document"
                      data-testid={`st-doc-open-${d.type}`}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  {d.source === 'manual' && (
                    <button
                      type="button"
                      onClick={() => handleDelete(d)}
                      className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      title="Supprimer ce document"
                      data-testid={`st-doc-delete-${d.type}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un document légal</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <Label htmlFor="doc-type">Type de document</Label>
              <Select value={upType} onValueChange={setUpType}>
                <SelectTrigger data-testid="st-doc-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="doc-edition-date">Date d&apos;édition du document</Label>
              <Input
                id="doc-edition-date"
                type="date"
                value={upDate}
                onChange={(e) => setUpDate(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                required
                data-testid="st-doc-edition-date"
              />
              <p className="text-xs text-slate-500 mt-1">
                Date figurant sur le document (émis par l&apos;organisme). Les 3 mois de validité démarrent à partir de cette date.
              </p>
            </div>

            <div>
              <Label htmlFor="doc-file">Fichier (PDF, JPG, PNG, HEIC · max 12 Mo)</Label>
              <Input
                id="doc-file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*"
                onChange={(e) => setUpFile(e.target.files?.[0] || null)}
                required
                data-testid="st-doc-file-input"
              />
              {upFile && (
                <p className="text-xs text-slate-500 mt-1">
                  {upFile.name} · {(upFile.size / 1024).toFixed(0)} Ko
                </p>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setUploadOpen(false)}
                disabled={uploading}
              >
                <X className="w-4 h-4 mr-1" /> Annuler
              </Button>
              <Button type="submit" disabled={uploading} data-testid="st-doc-upload-submit">
                {uploading
                  ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Envoi…</>
                  : <><Upload className="w-4 h-4 mr-1.5" />Ajouter</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <RequestAdditionalDocsDialog
        open={customReqOpen}
        onOpenChange={setCustomReqOpen}
        stId={sousTraitantId}
        stNom=""
        stEmail=""
        uploadedDocTypes={docs.filter((d) => !!d.url).map((d) => d.type)}
        onSent={() => { load(); if (onRefresh) onRefresh(); }}
      />
    </div>
  );
}
